import { Component, computed, inject, input, output } from '@angular/core';
import type { ModalAction } from '@purolator/ui-kit-angular';
import { ModalComponent, TypographyComponent } from '@purolator/ui-kit-angular';
import { TranslationService } from '../../../../core/i18n';
import { formatShipmentCreationDate } from '../../utils/format-get-labels-dates';

/**
 * Post-cancel confirmation dialog on Get Labels (OM-69 / Figma cancelled popup, no refund).
 * OM-890: when an associated pickup existed, body mentions that the pickup was cancelled too.
 */
@Component({
  selector: 'app-get-labels-cancelled-shipment-dialog',
  imports: [ModalComponent, TypographyComponent],
  templateUrl: './get-labels-cancelled-shipment-dialog.html',
  styleUrl: './get-labels-cancelled-shipment-dialog.scss',
})
export class GetLabelsCancelledShipmentDialog {
  private readonly translation = inject(TranslationService);

  readonly open = input(false);
  readonly trackingPin = input.required<string>();
  readonly shipmentCreationDate = input.required<string>();
  /**
   * True when the cancelled shipment had a scheduled courier pickup (OM-890 / ESN parity).
   */
  readonly hasAssociatedPickup = input(false);
  readonly backToHome = output<void>();

  protected readonly modalTitle = computed(() => {
    this.translation.revision();
    return this.translation.translate('createShipment.getLabels.cancelledShipment.title');
  });

  protected readonly bodyText = computed(() => {
    this.translation.revision();
    const params = {
      trackingPin: this.trackingPin().trim(),
      date: formatShipmentCreationDate(
        this.shipmentCreationDate().trim(),
        this.translation.locale(),
      ),
    };
    const bodyKey = this.hasAssociatedPickup()
      ? 'createShipment.getLabels.cancelledShipment.bodyWithPickup'
      : 'createShipment.getLabels.cancelledShipment.body';
    return this.translation.translate(bodyKey, params);
  });

  protected readonly actions = computed<ModalAction[]>(() => {
    this.translation.revision();
    return [
      {
        label: this.translation.translate('createShipment.getLabels.cancelledShipment.backToHome'),
        variant: 'primary',
        onClick: () => this.onBackToHome(),
      },
    ];
  });

  /**
   * Clears the completed journey and returns home (Figma Back to Home).
   */
  protected onBackToHome(): void {
    this.backToHome.emit();
  }
}
